import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { apiFetch } from "@/lib/api";
import { timeAgo } from "@/lib/format";
import { PageLoader, EmptyState } from "@/components/common";
import { LifeBuoy } from "lucide-react";

export function AdminSupport() {
  const qc = useQueryClient();
  const [showClosed, setShowClosed] = useState(false);
  const [replies, setReplies] = useState({});
  const { data: tickets, isLoading } = useQuery({
    queryKey: ["admin-support", showClosed],
    queryFn: () => apiFetch(showClosed ? "/admin/support/closed" : "/admin/support"),
  });

  const reply = async (id) => {
    const message = (replies[id] || "").trim();
    if (!message) return;
    try {
      await apiFetch(`/admin/support/${id}/reply`, { method: "POST", body: JSON.stringify({ message }) });
      toast.success("Reply sent");
      setReplies((r) => ({ ...r, [id]: "" }));
      qc.invalidateQueries({ queryKey: ["admin-support"] });
    } catch (err) { toast.error(err.message); }
  };
  const action = async (id, path, label) => {
    try { await apiFetch(`/admin/support/${id}/${path}`, { method: "POST" }); toast.success(label); qc.invalidateQueries({ queryKey: ["admin-support"] }); }
    catch (err) { toast.error(err.message); }
  };

  if (isLoading) return <PageLoader />;

  return (
    <div>
      <div className="flex gap-2">
        <button onClick={() => setShowClosed(false)} className={`rounded-full px-3 py-1.5 text-xs font-semibold ${!showClosed ? "bg-brand text-white" : "bg-secondary"}`}>Open</button>
        <button onClick={() => setShowClosed(true)} className={`rounded-full px-3 py-1.5 text-xs font-semibold ${showClosed ? "bg-brand text-white" : "bg-secondary"}`}>Closed</button>
      </div>
      <div className="mt-4 space-y-3">
        {tickets.length === 0 ? <EmptyState icon={LifeBuoy} title="No tickets" /> : tickets.map((t) => (
          <div key={t.id} className="rounded-2xl border border-border p-4" data-testid={`admin-ticket-${t.id}`}>
            <div className="flex items-center justify-between">
              <span className="font-semibold">{t.subject}</span>
              <span className="rounded-full bg-secondary px-2.5 py-0.5 text-xs font-semibold capitalize">{t.status}</span>
            </div>
            <p className="text-xs text-muted-foreground">{t.user_name || t.name} · {t.user_email || t.email} · {timeAgo(t.created_at)}</p>
            <p className="mt-2 text-sm">{t.message}</p>
            {t.replies?.map((r, i) => (
              <div key={i} className={`mt-2 rounded-xl p-2.5 text-sm ${r.by === "admin" ? "bg-price-soft" : "bg-secondary"}`}>
                <p className="text-[11px] font-semibold">{r.by === "admin" ? "Venda Team" : "User"} · {timeAgo(r.at)}</p>
                <p>{r.message}</p>
              </div>
            ))}
            {!showClosed && (
              <div className="mt-3 space-y-2">
                <textarea value={replies[t.id] || ""} onChange={(e) => setReplies((r) => ({ ...r, [t.id]: e.target.value }))} rows={2} placeholder="Reply to user…" className="w-full rounded-lg border border-input p-2 text-sm outline-none" data-testid={`reply-input-${t.id}`} />
                <div className="flex gap-2">
                  <button onClick={() => reply(t.id)} className="rounded-lg bg-price px-3 py-1.5 text-xs font-semibold text-white" data-testid={`send-reply-${t.id}`}>Send reply</button>
                  <button onClick={() => action(t.id, "escalate", "Escalated")} className="rounded-lg bg-amber-500 px-3 py-1.5 text-xs font-semibold text-white">Escalate</button>
                  <button onClick={() => action(t.id, "close", "Closed")} className="rounded-lg bg-secondary px-3 py-1.5 text-xs font-semibold">Close</button>
                </div>
              </div>
            )}
            {showClosed && <button onClick={() => action(t.id, "reopen", "Reopened")} className="mt-2 rounded-lg bg-secondary px-3 py-1.5 text-xs font-semibold">Reopen</button>}
          </div>
        ))}
      </div>
    </div>
  );
}
